<template>
  <modal-window :main-width="10">
    <h1>Shift Handover</h1>

    <div class="row" v-show="displayBaseData">
      <div class="col-6 my-auto align-items-center" v-if="!currentShift || !currentShift.supervisor">
        <div class="p-5 text-center font-weight-bold" style="color: #aaa; font-size: 2.5rem">
          <i class="far fa-shapes"></i>
          <br />
          NO CURRENT SHIFT
        </div>
      </div>

      <div class="col-6" v-else>
        <div class="label mt-2 mb-1 text-center">Current Shift</div>
        <hr />

        <div class="label mt-2 mb-1">Supervisor</div>
        <v-select
          :options="availableSupervisors"
          label="displayName"
          :clearable="false"
          :disabled="true"
          :reduce="(user) => user.id"
          placeholder="Please select a supervisor..."
          v-model="currentShift.supervisor.id"
        >
          <template #open-indicator="{}">
            <span></span>
          </template>
        </v-select>

        <div class="label mt-2 mb-1">Shift Type</div>
        <v-select
          :options="shiftTypes"
          label="name"
          :disabled="true"
          :clearable="false"
          placeholder="No shift type was selected"
          v-model="currentShift.shiftType"
        >
          <template #open-indicator="{}">
            <span></span>
          </template>
        </v-select>

        <div class="row">
          <div class="col-6">
            <div class="label mt-2 mb-1">From</div>
            {{ shiftStart }}
          </div>

          <div class="col-6">
            <div class="label mt-2 mb-1">To</div>
            {{ shiftEnd }}
          </div>
        </div>
      </div>

      <div class="col-6">
        <div class="label mt-2 mb-1 text-center">Next Shift</div>
        <hr />

        <div class="label mt-2 mb-1">Supervisor</div>
        <div :class="{ required: !supervisorId }">
          <v-select
            v-model="supervisorId"
            :options="availableSupervisors"
            label="displayName"
            :reduce="(user) => user.id"
            :clearable="false"
            :disabled="false"
            placeholder="Please select a supervisor..."
          ></v-select>
        </div>

        <div class="label mt-2 mb-1">Shift Type</div>
        <v-select
          :options="shiftTypes"
          label="name"
          placeholder="Please select a shift type..."
          :reduce="(shiftType) => shiftType.id"
          @input="changeNextShiftType"
          :clearable="false"
          :value="shiftTypeId"
          :searchable="false"
        ></v-select>

        <div class="row">
          <div class="col-6">
            <div class="label mt-2 mb-1">Next Shift From</div>
            {{ nextShiftStart }}
          </div>

          <div class="col-6">
            <div class="label mt-2 mb-1">To</div>
            {{ nextShiftEnd }}
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="displayBaseData">
      <div class="col-12">
        <div class="label mt-2 mb-1">Comment</div>
        <textarea class="form-control" rows="4" v-model="comment"></textarea>
      </div>
    </div>

    <div class="row">
      <div
        class="col-12 text-center mt-2 k-cursor-pointer"
        @click="displayBaseData = !displayBaseData"
        v-b-tooltip.hover
        title="Toggle base data"
      >
        <i class="fa-2x far fa-angle-up" v-if="displayBaseData"></i>
        <i class="fa-2x far fa-angle-down" v-if="!displayBaseData"></i>
        <hr class="m-0" />
      </div>
    </div>

    <div class="row"></div>

    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h2 class="mt-3">Board Items</h2>
        <small>Info: Done items will be archived on Shift Handover, Items in progress will be moved to the Tasks list</small>
      </div>
      <div>
        <select class="form-control" v-model="grouping">
          <option value="category">Group by Category</option>
          <option value="label">Group by Label</option>
        </select>
      </div>
    </div>

    <div style="" class="mt-3">
      <div v-if="!groupedItems.length">
        <div class="p-5 text-center font-weight-bold" style="color: #aaa; font-size: 2.5rem">
          <i class="far fa-shapes"></i>
          <br />
          NO BOARDITEMS
        </div>
      </div>

      <div v-else>
        <table class="table table-condensed table-sm boardItemTable">
          <colgroup>
            <col style="width: 10%" />
            <col style="width: 40%" />
            <col style="width: 40%" />
            <col style="width: 10%" />
          </colgroup>
          <thead>
            <tr>
              <th></th>
              <th>Title and Description</th>
              <th>Last Comment</th>
              <th>Daily Report</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(group, index) in groupedItems">
              <tr :key="index" class="groupHeader">
                <td colspan="5">
                  <i class="far" :class="group.icon"></i>
                  {{ group.title }}
                </td>
              </tr>

              <tr v-for="item in group.items" :key="item.id + group.id">
                <td style="vertical-align: middle" class="pl-5">
                  <BoardItemStatus :status="item.boardItemStatus"></BoardItemStatus>
                </td>
                <td>
                  <div v-if="grouping == 'label'" class="font-weight-bold">
                    {{ item.boardItemCategory.name }}
                  </div>
                  <BoardItemLabels :item="item" class="mt-2"></BoardItemLabels>
                  <small>
                    <strong>{{ item.boardItemType.name }}</strong>
                  </small>
                  <br />
                  {{ item.title }}
                  <br />

                  <small>
                    <div v-simplify-html="item.description" />
                  </small>
                </td>
                <td>
                  <small>
                    {{ lastComment(item) }}
                  </small>
                </td>
                <td>
                  <input type="checkbox" @change="changeItem(item.id)" :checked="relevantItemIds.indexOf(item.id) !== -1" />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <template v-slot:sidebar>
      <div class="align-self-end flex-grow-1">
        <button :disabled="!supervisorId || !shiftTypeId" class="btn btn-block btn-sm btn-success" @click="confirmHandover()">
          <i class="far fa-save"></i>
          Handover to next shift
        </button>
      </div>
    </template>
  </modal-window>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { BoardModule, BoardStore } from '@/store/board';
  import ModalWindow from '@/components/ModalWindow.vue';
  import { DateTime } from 'luxon';
  import _, { sortBy } from 'lodash';
  import {
    AvailabilityEntityType,
    BoardItemCategoryDto,
    BoardItemPriorityDto,
    LabelDto,
    ShiftDto,
    ShiftHandoverDto,
    ShiftTypeDto,
  } from '@/apiclient/apiclient_generated';
  import { GlobalHelper } from '@/helpers/global-helper';
  import { UserModule, UserStore } from '@/store/user';
  import BoardItemStatus from '@/components/BoardItem/BoardItemStatus.vue';
  import BoardItemLabels from '@/components/Labels/BoardItemLabels.vue';
  import { SettingsModule, SettingsStore } from '@/store/settings';

  @Component({
    components: { BoardItemLabels, BoardItemStatus, ModalWindow },
  })
  export default class ShiftHandover extends Vue {
    private settingsStore: SettingsModule = SettingsStore;
    private boardStore: BoardModule = BoardStore;
    private userStore: UserModule = UserStore;

    private shiftStart = '';
    private shiftEnd = '';
    private nextShiftStart = '';
    private nextShiftEnd = '';
    private shiftTypeId = '';
    private supervisorId = '';
    private displayBaseData = true;
    private comment = '';

    private shiftHandover: ShiftHandoverDto = new ShiftHandoverDto();
    private relevantItemIds: string[] = [];

    private grouping = this.boardStore.board.shiftHandoverGrouping ? this.boardStore.board.shiftHandoverGrouping : 'category';

    async mounted() {
      const userParams = {
        entityType: AvailabilityEntityType.Board,
        entityId: this.boardStore.board.id,
      };

      await this.userStore.getAvailableUsers(userParams);
      await this.settingsStore.loadShiftValidationSettings(this.boardStore.currentBoardId);

      //leave the following line intact => used for testing
      //Settings.now = () => new Date(2020, 8, 4, 6, 30).valueOf();

      this.shiftStart = DateTime.fromISO(
        this.boardStore.board.currentShift ? this.boardStore.board.currentShift.dateFrom : '2020-01-01 00:00'
      ).toFormat('yyyy-MM-dd HH:mm');

      this.shiftEnd = DateTime.utc().toLocal().toFormat('yyyy-MM-dd HH:mm');
      this.nextShiftStart = DateTime.utc().toLocal().plus({ minutes: 1 }).toFormat('yyyy-MM-dd HH:mm');

      //find entry for next ShiftType
      const baseDate = DateTime.utc(); // Date to compare to
      const shiftTypesWithDistance = [];

      //calculate the distance from NOW() to ShiftType Start for each shift type and choose the shift type with
      //the smallest distance with an overlap for one hour
      //e.g. Shift starts at 06:00, now it's 06:30 => choose the shift which starts at 06:00, because it's in the 1hr window
      this.boardStore.board.shiftTypes.forEach((type) => {
        let dateShiftType = baseDate.set({
          hour: parseInt(type.defaultTimeFrom.substr(0, 2)),
          minute: parseInt(type.defaultTimeFrom.substr(3, 2)),
        });

        if (DateTime.local().isInDST) {
          dateShiftType = baseDate.set({
            hour: parseInt(type.timeFromDst.substr(0, 2)),
            minute: parseInt(type.timeFromDst.substr(3, 2)),
          });
        }

        if (dateShiftType < baseDate && dateShiftType.diff(baseDate, 'seconds').seconds < -3600) {
          dateShiftType = dateShiftType.plus({ days: 1 });
        }

        const distance = dateShiftType.diff(baseDate, 'seconds').seconds;

        shiftTypesWithDistance.push({ distance: distance, timeTo: dateShiftType, shiftType: type });
      });

      //set the shiftType to the shiftType with the smallest distance and the
      //shiftEnd to the end of this shiftType
      const chosenShiftType = sortBy(shiftTypesWithDistance, 'distance')[0];

      this.shiftTypeId = chosenShiftType.shiftType.id;
      this.changeNextShiftType(this.shiftTypeId);

      const relevantItems = [];

      const relevantForShiftReportTypeIds = this.settingsStore.shiftValidationSettings.types
        .filter((_) => _.isRelevantForShiftReport)
        .map((_) => _.typeId);

      const reportItems = [...this.boardStore.boardItems, ...this.boardStore.delegatedBoardItems, ...this.boardStore.sharedBoardItems];

      reportItems.forEach((_) => {
        if (
          relevantForShiftReportTypeIds.indexOf(_.boardItemTypeId) !== -1 ||
          _.priority == BoardItemPriorityDto.High ||
          _.isRelevantForReport
        ) {
          relevantItems.push(_.id);
        }
      });

      this.relevantItemIds = relevantItems;
    }

    changeNextShiftType(id: string) {
      const shiftType = this.boardStore.board.shiftTypes.find((_) => _.id == id);
      const baseDate = DateTime.utc(); // Date to compare to

      let dateShiftType = baseDate.set({
        hour: parseInt(shiftType.defaultTimeTo.substr(0, 2)),
        minute: parseInt(shiftType.defaultTimeTo.substr(3, 2)),
      });

      if (DateTime.local().isInDST) {
        dateShiftType = baseDate.set({
          hour: parseInt(shiftType.timeToDst.substr(0, 2)),
          minute: parseInt(shiftType.timeToDst.substr(3, 2)),
        });
      }

      if (dateShiftType < baseDate) {
        dateShiftType = dateShiftType.plus({ days: 1 });
      }

      this.nextShiftEnd = dateShiftType.toLocal().toFormat('yyyy-MM-dd HH:mm');
      this.shiftTypeId = id;
    }

    confirmHandover() {
      this.$bvModal
        .msgBoxConfirm('Are you sure you want to handover to the next shift?', {
          title: 'Confirm shift handover',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'success',
          okTitle: 'Yes, handover shift',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: true,
          centered: false,
        })
        .then((value) => {
          if (value) {
            this.doHandover();
          }
        })
        .catch(() => {
          // An error occurred
        });
    }

    async doHandover() {
      const dates = {
        shiftStart: DateTime.fromFormat(this.shiftStart, 'yyyy-MM-dd HH:mm').toUTC().toISO(),
        shiftEnd: DateTime.fromFormat(this.shiftEnd, 'yyyy-MM-dd HH:mm').toUTC().toISO(),
        nextStart: DateTime.fromFormat(this.nextShiftStart, 'yyyy-MM-dd HH:mm').toUTC().toISO(),
        nextEnd: DateTime.fromFormat(this.nextShiftEnd, 'yyyy-MM-dd HH:mm').toUTC().toISO(),
      };

      this.shiftHandover.currentShiftTo = dates.shiftEnd;
      this.shiftHandover.nextShiftFrom = dates.nextStart;
      this.shiftHandover.nextShiftTo = dates.nextEnd;
      this.shiftHandover.comment = 'Auto';
      this.shiftHandover.boardGuid = this.boardStore.board.id;
      this.shiftHandover.relevantItems = this.relevantItemIds;
      this.shiftHandover.shiftTypeId = this.shiftTypeId;
      this.shiftHandover.comment = this.comment;
      this.shiftHandover.supervisorId = this.supervisorId;

      await this.boardStore.handoverShift(this.shiftHandover);
      this.$bvModal.hide('shiftHandover');

      GlobalHelper.NotifySuccess('Shift was handed over...');
    }

    get availableSupervisors() {
      const users = this.userStore.availableUsers?.filter((_) => _.isShiftSupervisor);
      return this.lodash.orderBy(users, 'displayName');
    }

    get shiftTypes(): ShiftTypeDto[] {
      return this.lodash.sortBy(this.boardStore.board.shiftTypes, 'sortOrder');
    }

    get currentShift(): ShiftDto {
      return this.boardStore.board.currentShift;
    }

    get groupedByLabel() {
      const allItems = [...this.boardStore.boardItems, ...this.boardStore.delegatedItems, ...this.boardStore.sharedBoardItems];

      let distinctLabels = _.uniqBy(_.flatten(allItems.map((_) => _.labels)), 'id');

      const emptyLabel = Object.assign(new LabelDto(), { id: '', title: '~ No Label ~', color: '357ebd', icon: null });
      distinctLabels.push(emptyLabel);
      distinctLabels = _.orderBy(distinctLabels, [(label) => label.title.toLowerCase()]);

      const groups = distinctLabels.map((_) => {
        const group: any = {};
        group.icon = _.icon;
        group.title = _.title;
        group.id = _.id;
        group.items = allItems.filter((item) => item.labels.map((label) => label.id).indexOf(_.id) !== -1);

        if (_.id === '') {
          group.items = allItems.filter((item) => item.labels.length == 0);
        }

        return group;
      });

      return groups;
    }

    get groupedByCategory(): any[] {
      const allItems = [...this.boardStore.boardItems, ...this.boardStore.delegatedItems, ...this.boardStore.sharedBoardItems];

      const categories = this.boardStore.boardItemCategories as BoardItemCategoryDto[];
      const groups = categories.map((_) => {
        const group: any = {};
        group.icon = _.icon;
        group.title = _.name;
        group.id = _.id;
        group.items = allItems.filter((item) => item.boardItemCategoryId == _.id);
        return group;
      });

      return groups.filter((_) => _.items.length);
    }

    get groupedItems(): any[] {
      if (this.grouping == 'category') return this.groupedByCategory;
      else return this.groupedByLabel;
    }

    changeItem(id: string) {
      if (this.relevantItemIds.indexOf(id) !== -1) {
        this.relevantItemIds = this.relevantItemIds.filter((_) => _ !== id);
      } else {
        this.relevantItemIds.push(id);
      }
    }

    private lastComment(item) {
      if (item.comments?.length) {
        const userComments = item.comments.filter((_) => !_.isSystemComment);

        if (userComments?.length) {
          return this.lodash.orderBy(userComments, 'createdDate', 'desc')[0].text;
        }
      }

      return '';
    }
  }
</script>

<style scoped lang="scss">
  .boardItemTable {
    th {
      font-size: 1.2rem;
      color: #888;
    }

    tr:not(.groupHeader):hover {
      td {
        background: darken(#f4f4f7, 5%);
      }
    }

    td {
      vertical-align: middle;
      border: none;
      border-bottom: solid 1px #eaeaea;
    }

    .groupHeader {
      font-size: 1.1rem;
      font-weight: bold;
      color: #888;

      td {
        padding-top: 8px;
        padding-bottom: 8px;
      }
    }
  }

  .required {
    border: 2px solid red;
    border-radius: 5px;
  }
</style>
